import { defineStore } from 'pinia'
import { v4 as uuidv4 } from 'uuid'
import {
  addFontFile,
  deleteFontFile,
  isDesktop,
  loadResourceLibrary,
  migrateFilesToDesktop,
  saveResourceLibrary,
} from '../services/resourceStorage.js'

const STORAGE_KEY = 'hasp-font-library'

// Project font library — metadata only. It owns none of the concerns that
// made the earlier version of this store a rendering cache in disguise (a
// `size` to draw at, a `data` blob duplicating what's now in FontDB,
// `loadedFamilies`/FontFace state). Actually loading and rendering a font is
// the Designer rendering layer's job — see src/services/DesignerFontLoader.js.
// FontStore only ever does CRUD + lookup + metadata, and orchestrates handing
// bytes to FontDB (still just storage, not rendering) on upload/removal.
//
// No resource-registration line exists on the wire for fonts (unlike
// datasets) — a widget's Font Family is exported as the literal `file` path
// (see Exporter._processFonts()), resolved lazily by the firmware
// FontManager exactly like images are referenced by filename with no
// registration step. `file` (on-device path, e.g. "L:/7segment.ttf") is
// therefore this resource's whole export identity; `id` is a Designer-only
// display/dedup convenience. `size` does NOT live here: a Font Resource is a
// source file, never tied to one size (see label.js's `font` + `text_font` —
// the widget's own `text_font` is the pixel size that font is rendered at),
// since one font file is reusable by many widgets at different sizes.

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

export const useFontStore = defineStore('fonts', {
  // The browser library loads synchronously; the desktop app's SQLite
  // library (metadata and bytes) arrives through load() at startup.
  state: () => ({
    fonts: isDesktop() ? [] : loadFromStorage(),
    loaded: !isDesktop(),
  }),
  getters: {
    all: (state) => state.fonts,
  },
  actions: {
    /** Load the desktop app's SQLite library (no-op in the browser, which loads synchronously). */
    async load() {
      if (!isDesktop()) return
      this.fonts = await loadResourceLibrary('fonts', STORAGE_KEY, { migrateEntries: migrateFilesToDesktop('fonts') })
      this.loaded = true
    },
    _persist() {
      // Never let a save before load() replace the stored library with a partial one.
      if (!this.loaded) return
      try {
        saveResourceLibrary('fonts', STORAGE_KEY, this.fonts)
      } catch (e) {
        console.warn('Failed to persist font library:', e)
      }
    },
    // Lowest free "font"/"font2"/"font3"... id built off `base` — used both for
    // brand-new fonts (base 'font') and duplicates (base = the source's own id).
    _nextAvailableId(base) {
      const used = new Set(this.fonts.map(f => f.id))
      if (!used.has(base)) return base
      let n = 2
      while (used.has(`${base}${n}`)) n++
      return `${base}${n}`
    },

    // --- CRUD ---

    addFont() {
      const font = {
        uuid: uuidv4(),
        id: this._nextAvailableId('font'),
        name: 'New Font',
        file: '',
        storageId: null,        // FontDB row id for the uploaded file, or null
        originalFilename: null, // e.g. "7segment.ttf"
        type: null,             // the uploaded File's own MIME type
        metadata: {},           // small descriptive info about the file (see setFontFile)
      }
      this.fonts.push(font)
      this._persist()
      return font.uuid
    },
    removeFont(uuid) {
      const font = this.getFont(uuid)
      if (!font) return
      this.fonts.splice(this.fonts.indexOf(font), 1)
      this._persist()
      // Duplicates can share a storageId (see duplicateFont) — only free the
      // stored file once nothing else references it.
      const stillReferenced = font.storageId != null && this.fonts.some(f => f.storageId === font.storageId)
      if (font.storageId != null && !stillReferenced) deleteFontFile(font.storageId)
    },
    duplicateFont(uuid) {
      const src = this.fonts.find(f => f.uuid === uuid)
      if (!src) return null
      const copy = {
        ...JSON.parse(JSON.stringify(src)),
        uuid: uuidv4(),
        id: this._nextAvailableId(src.id),
        name: src.name + ' (copy)',
        // storageId is intentionally copied as-is (both resources point at
        // the same stored file) rather than duplicated in FontDB — cheaper,
        // and correct: a later setFontFile() on either one only replaces
        // *that* resource's storageId, never mutates the shared file in place.
      }
      this.fonts.push(copy)
      this._persist()
      return copy.uuid
    },
    getFont(uuid) {
      return this.fonts.find(f => f.uuid === uuid) ?? null
    },
    updateFont(uuid, patch) {
      const font = this.fonts.find(f => f.uuid === uuid)
      if (!font) return
      Object.assign(font, patch)
      this._persist()
    },

    // --- File storage (still metadata/CRUD — no FontFace, no loading) ---

    /**
     * Store an uploaded File's bytes in FontDB and point this resource at
     * them. Purely storage: reads the file, delegates to FontDB, updates
     * metadata. Never touches FontFace/document.fonts — that's
     * DesignerFontLoader's job once it needs to actually render this
     * resource.
     */
    async setFontFile(uuid, file) {
      const font = this.getFont(uuid)
      if (!font) return
      const data = await readFileAsDataURL(file)
      const previousStorageId = font.storageId
      const storageId = await addFontFile(data, file.name, file.type)
      this.updateFont(uuid, {
        storageId,
        originalFilename: file.name,
        type: file.type,
        metadata: { fileSize: file.size },
      })
      // Only this resource pointed at the old file (a fresh upload always
      // gets its own new row) — safe to free it, unless a duplicate shares it.
      if (previousStorageId != null && !this.fonts.some(f => f.storageId === previousStorageId)) {
        deleteFontFile(previousStorageId)
      }
    },

  },
})

function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}
